import { useState, useMemo } from 'react';
import { levelColors, levelTranslations } from '../ConstFuncs';
import SchoolServiceAdmin from '../../services/SchoolServiceAdmin';
import { hasSpacingIssue } from '../../utils/textUtils';

const StudentsTable = ({ allStudents, refreshAllStudents }) => {
  const [sortConfig, setSortConfig] = useState({
    key: null,
    direction: 'asc',
  });

  // Editing States
  const [editingEnabled, setEditingEnabled] = useState(false);
  const [editingStudentId, setEditingStudentId] = useState(null);
  const [editingField, setEditingField] = useState('');
  const [editingValue, setEditingValue] = useState('');
  const [updateMessage, setUpdateMessage] = useState('');

  // Sorting logic
  const sortedStudents = useMemo(() => {
    if (!sortConfig.key) return allStudents;

    return [...allStudents].sort((a, b) => {
      let valA = a[sortConfig.key];
      let valB = b[sortConfig.key];

      if (Array.isArray(valA)) valA = valA.join(', ');
      if (Array.isArray(valB)) valB = valB.join(', ');

      const isNullA =
        valA === null || valA === undefined || valA === '';
      const isNullB =
        valB === null || valB === undefined || valB === '';

      if (isNullA && isNullB) return 0;
      if (isNullA) return sortConfig.direction === 'asc' ? 1 : -1;
      if (isNullB) return sortConfig.direction === 'asc' ? -1 : 1;

      if (typeof valA === 'string') {
        return sortConfig.direction === 'asc'
          ? valA.localeCompare(valB)
          : valB.localeCompare(valA);
      }

      if (typeof valA === 'number') {
        return sortConfig.direction === 'asc'
          ? valA - valB
          : valB - valA;
      }

      return 0;
    });
  }, [allStudents, sortConfig]);

  const handleSort = (key) => {
    setSortConfig((prev) => ({
      key,
      direction:
        prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc',
    }));
  };

  // Render editable cell
  const renderCell = (student, field, value) => {
    const isEditing =
      editingEnabled &&
      editingStudentId === student.id &&
      editingField === field;

    if (isEditing) {
      return (
        <input
          autoFocus
          value={editingValue}
          onChange={(e) => setEditingValue(e.target.value)}
          onBlur={async () => {
            const confirmed = window.confirm(`Update ${field}?`);
            if (!confirmed) {
              setEditingStudentId(null);
              setEditingField('');
              return;
            }

            try {
              await SchoolServiceAdmin.updateStudentField(
                student.id,
                field,
                editingValue,
              );
              setUpdateMessage('Field updated successfully');
              refreshAllStudents();
            } catch (error) {
              setUpdateMessage(
                error.response?.data?.message || 'Update failed',
              );
            }

            setTimeout(() => setUpdateMessage(''), 3000);
            setEditingStudentId(null);
            setEditingField('');
          }}
          onKeyDown={(e) => e.key === 'Enter' && e.target.blur()}
          className="border border-blue-400 bg-blue-50 rounded px-2 py-1 w-full focus:ring focus:ring-blue-300"
        />
      );
    }

    return (
      <span
        className={
          editingEnabled
            ? 'cursor-pointer hover:bg-blue-50 px-2 py-1 block'
            : typeof value === 'string' && hasSpacingIssue(value)
              ? 'bg-yellow-200 text-red-700 font-semibold px-1 rounded'
              : ''
        }
        onClick={() => {
          if (!editingEnabled) return;
          setEditingStudentId(student.id);
          setEditingField(field);
          setEditingValue(value || '');
        }}
      >
        {value}
      </span>
    );
  };

  return (
    <>
      {/* Enable Editing Toggle */}
      <div className="flex items-center justify-center w-full py-4">
        <label className="relative inline-flex items-center cursor-pointer">
          <input
            type="checkbox"
            className="sr-only peer"
            checked={editingEnabled}
            onChange={(e) => setEditingEnabled(e.target.checked)}
          />
          <div
            className="
              w-11 h-6 bg-gray-300 rounded-full peer 
              peer-checked:bg-blue-500 peer-focus:ring-2 
              peer-focus:ring-blue-300 transition-all duration-300
            "
          ></div>
          <div
            className="
              absolute left-0.5 top-0.5 w-5 h-5 bg-white rounded-full 
              transition-all duration-300 peer-checked:translate-x-5 
              peer-checked:scale-110 peer-checked:shadow-md
            "
          ></div>
          <span className="ml-3 text-gray-800 font-medium select-none">
            Enable Editing
          </span>
        </label>
      </div>
      <div className="my-4 max-h-[600px] overflow-y-auto border rounded">
        <table className="min-w-full">
          <thead className="bg-gray-800 text-white sticky top-0">
            <tr>
              <th className="py-2 px-4">#</th>

              {[
                { key: 'id', label: 'ID' },
                { key: 'firstName', label: 'First Name' },
                { key: 'lastName', label: 'Last Name' },
                {
                  key: 'firstNameArabic',
                  label: 'First Name Arabic',
                },
                { key: 'lastNameArabic', label: 'Last Name Arabic' },
                { key: 'email', label: 'Parents Email' },
                {
                  key: 'lastParentReportEmailSent',
                  label: 'Last Parent Report Sent',
                },

                {
                  key: 'classDaysOfWeek',
                  label: 'Class Days Of Week',
                },
                { key: 'teacherUserName', label: 'Teacher Username' },
                {
                  key: 'teacherArabicName',
                  label: 'Teacher Arabic Name',
                },
                { key: 'level', label: 'Student Level' },
              ].map((col) => (
                <th
                  key={col.key}
                  className="py-2 px-2 cursor-pointer select-none"
                  onClick={() => handleSort(col.key)}
                >
                  {col.label}
                  {sortConfig.key === col.key &&
                    (sortConfig.direction === 'asc' ? ' 🔼' : ' 🔽')}
                </th>
              ))}
            </tr>
          </thead>

          <tbody>
            {sortedStudents.map((student, index) => (
              <tr className="border-b text-center" key={student.id}>
                {/* Row Number */}
                <td className="py-2 px-4 font-medium text-gray-500">
                  {index + 1}
                </td>

                {/* ID (non-editable) */}
                <td className="py-2 px-4">{student.id}</td>

                {/* Editable fields */}
                <td>
                  {renderCell(
                    student,
                    'firstName',
                    student.firstName,
                  )}
                </td>
                <td>
                  {renderCell(student, 'lastName', student.lastName)}
                </td>
                <td>
                  {renderCell(
                    student,
                    'firstNameArabic',
                    student.firstNameArabic,
                  )}
                </td>
                <td>
                  {renderCell(
                    student,
                    'lastNameArabic',
                    student.lastNameArabic,
                  )}
                </td>
                <td>{renderCell(student, 'email', student.email)}</td>
                <td className="py-2 px-4 text-sm">
                  {student.lastParentReportEmailSent ? (
                    <span className="px-2 py-1 rounded bg-green-100 text-green-700 font-medium">
                      {new Date(
                        student.lastParentReportEmailSent,
                      ).toLocaleString('de-DE')}
                    </span>
                  ) : (
                    <span className="px-2 py-1 rounded bg-gray-200 text-gray-600">
                      Never sent
                    </span>
                  )}
                </td>

                {/* Class days (array, not editable) */}
                <td className="py-2 px-4">
                  {student.teacher?.teacherClassDaysOfWeek.join(', ')}
                </td>

                {/* Teacher fields (not editable here) */}
                <td className="py-2 px-4">
                  {student.teacher?.username}
                </td>
                <td className="py-2 px-4">
                  {student.teacher?.firstName}{' '}
                  {student.teacher?.lastName}
                </td>

                {/* Level (color box, not editable) */}
                <td
                  className={`py-2 font-semibold text-gray-300 ${
                    student.level != null
                      ? levelColors[student.level]
                      : 'bg-slate-600'
                  }`}
                >
                  {student.level != null
                    ? levelTranslations[student.level]
                    : 'لم يتم الاختيار بعد'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {/* Update Message Popup */}
        {updateMessage && (
          <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-40 z-50">
            <div
              className={`bg-white rounded-lg shadow-lg px-6 py-4 w-80 text-center border ${
                updateMessage.toLowerCase().includes('success')
                  ? 'border-green-400'
                  : 'border-red-400'
              }`}
            >
              <h3
                className={`text-lg font-semibold mb-2 ${
                  updateMessage.toLowerCase().includes('success')
                    ? 'text-green-700'
                    : 'text-red-700'
                }`}
              >
                {updateMessage.toLowerCase().includes('success')
                  ? '✅ Success'
                  : '⚠️ Error'}
              </h3>

              <p className="text-gray-800">{updateMessage}</p>

              <div className="mt-4">
                <button
                  onClick={() => setUpdateMessage('')}
                  className="px-4 py-2 rounded bg-blue-600 text-white hover:bg-blue-700"
                >
                  OK
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default StudentsTable;
